// src/components/network/messaging/MessagesList.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Clock, MessageSquare, Plus } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { db } from '../../../firebase/config';
import { 
  collection, query, where, orderBy, limit, 
  getDocs, doc, getDoc 
} from 'firebase/firestore';
import MessagePreview from './MessagePreview';

const MessagesList = ({ onSelectConversation }) => {
  const { currentUser } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  useEffect(() => {
    const fetchConversations = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        // Query conversations where current user is a participant
        const conversationsQuery = query(
          collection(db, 'conversations'),
          where('participants', 'array-contains', currentUser.uid),
          orderBy('lastMessageAt', 'desc'),
          limit(50)
        );
        
        const conversationsSnapshot = await getDocs(conversationsQuery);
        
        // Process each conversation
        const conversationsData = [];
        for (const conversationDoc of conversationsSnapshot.docs) {
          const conversation = conversationDoc.data();
          
          // Find the other participant
          const otherParticipantId = conversation.participants.find(id => id !== currentUser.uid);
          
          // Get participant info (could be user or business)
          let participantInfo = { id: otherParticipantId, name: 'Unknown' };
          
          try {
            // First try to get business info
            const businessRef = doc(db, 'businesses', otherParticipantId);
            const businessSnap = await getDoc(businessRef);
            
            if (businessSnap.exists()) {
              const businessData = businessSnap.data();
              participantInfo = {
                id: otherParticipantId,
                name: businessData.name,
                type: 'business',
                logoUrl: businessData.businessLogoUrl || null,
                industry: businessData.industry || null
              };
            } else {
              // Try to get user info
              const userRef = doc(db, 'users', otherParticipantId);
              const userSnap = await getDoc(userRef);
              
              if (userSnap.exists()) {
                const userData = userSnap.data();
                participantInfo = {
                  id: otherParticipantId,
                  name: userData.displayName || userData.email,
                  type: 'user',
                  email: userData.email,
                  photoURL: userData.photoURL || null
                };
              }
            }
          } catch (err) {
            console.error('Error fetching participant info:', err);
          }
          
          conversationsData.push({
            id: conversationDoc.id,
            ...conversation,
            lastMessageAt: conversation.lastMessageAt?.toDate() || new Date(),
            participant: participantInfo,
            unreadCount: conversation.unreadCount?.[currentUser.uid] || 0
          });
        }
        
        setConversations(conversationsData);
      } catch (err) {
        console.error('Error fetching conversations:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchConversations();
  }, [currentUser]);
  
  // Filter conversations based on search term
  const filteredConversations = conversations.filter(conv => 
    conv.participant.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  if (!currentUser) {
    return (
      <div className="p-4 text-center bg-gray-50 rounded-lg">
        <MessageSquare size={40} className="mx-auto text-gray-300 mb-2" />
        <p className="text-gray-600">Sign in to access messages</p>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-medium mb-4">Messages</h2>
        
        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            className="pl-10 pr-4 py-2 w-full rounded-lg text-sm border border-gray-200 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
            placeholder="Search messages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        {/* New message button */}
        <Link 
          to="/network/messages/new"
          className="mt-3 flex items-center justify-center w-full px-3 py-2 text-sm bg-[#190192] text-white rounded-lg hover:bg-[#2C0DB5]"
        >
          <Plus size={16} className="mr-2" />
          New Message
        </Link>
      </div>
      
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse flex p-3">
                <div className="w-10 h-10 bg-gray-200 rounded-full mr-3"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="p-8 text-center">
            {searchTerm ? (
              <>
                <Search size={40} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-600 mb-2">No matching conversations</p>
                <button 
                  className="text-[#190192] text-sm"
                  onClick={() => setSearchTerm('')}
                >
                  Clear search
                </button>
              </>
            ) : (
              <>
                <MessageSquare size={40} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-600 mb-2">No conversations yet</p>
                <p className="text-gray-500 text-sm">
                  Start messaging businesses to connect and collaborate
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredConversations.map(conversation => (
              <MessagePreview 
                key={conversation.id}
                conversation={conversation}
                onClick={() => onSelectConversation(conversation)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesList;